// externals
import {
    createCorrelationMiddleware as createSharedCorrelationMiddleware,
    createReduxActionSpineMiddleware,
    getActionStatus,
    ACTION_PATTERNS,
    ACTION_STATUS
} from '@makemydeal/dr-shared-store';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';

// types
import type { Middleware } from 'redux';
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';

export const PAYMENT_ACTIONS = {
    PAYMENT_REQUESTED: 'PAYMENT_REQUESTED',
    PAYMENT_FAILURE: 'PAYMENT_FAILURE',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    UPDATE_PAYMENT_FEES_OVERRIDE_PAYMENT_FAILED: 'UPDATE_PAYMENT_FEES_OVERRIDE_PAYMENT_FAILED',
    UPDATED_TOTAL_TAX_UPFRONT_OVERRIDE_PAYMENT_FAILED: 'UPDATED_TOTAL_TAX_UPFRONT_OVERRIDE_PAYMENT_FAILED',
    VEHICLE_PROTECTION_PRODUCT_CHANGES_PAYMENT_CALL_PAYMENT_FAILED:
        'VEHICLE_PROTECTION/PRODUCT_CHANGES_PAYMENT_CALL_PAYMENT_FAILED',
    APPLY_ROLL_TO_OPTIONS_PAYMENT_FAILED: 'APPLY_ROLL_TO_OPTIONS_PAYMENT_FAILED'
} as const;

/**
 * TEMPORARY WORKAROUND: Tag external library actions
 *
 * The @makemydeal/dr-offer-redux library dispatches actions without telemetry.
 * This function detects known external library actions and tags them with dispatchedBy metadata.
 *
 * TODO: Remove this when US1815738 is complete (external library updated with telemetry)
 * @see https://rally1.rallydev.com/userstory/US1815738
 *
 * @param actionType - The Redux action type to check
 * @returns true if this is a known external library action
 */
export function isExternalLibraryPaymentAction(actionType: string): boolean {
    // Exact match for known payment actions
    switch (actionType) {
        case PAYMENT_ACTIONS.PAYMENT_REQUESTED:
        case PAYMENT_ACTIONS.PAYMENT_FAILURE:
        case PAYMENT_ACTIONS.PAYMENT_RECEIVED:
        case PAYMENT_ACTIONS.UPDATE_PAYMENT_FEES_OVERRIDE_PAYMENT_FAILED:
        case PAYMENT_ACTIONS.UPDATED_TOTAL_TAX_UPFRONT_OVERRIDE_PAYMENT_FAILED:
        case PAYMENT_ACTIONS.VEHICLE_PROTECTION_PRODUCT_CHANGES_PAYMENT_CALL_PAYMENT_FAILED:
        case PAYMENT_ACTIONS.APPLY_ROLL_TO_OPTIONS_PAYMENT_FAILED:
            return true;

        // Pattern match: any action ending with _PAYMENT_FAILED
        default:
            return actionType.endsWith(ACTION_PATTERNS.PAYMENT_FAILED_SUFFIX);
    }
}

/**
 * Dash-specific correlation middleware
 *
 * **Why Two Correlation Middlewares?**
 *
 * @makemydeal/dr-shared-store - Base Implementation:
 * - Generic correlation tracking for ANY Redux store
 * - No business context dependencies
 * - Used by: dash, sales-checkin, choose-vehicle, open-deal
 *
 * @makemydeal/dr-dash-store - Dash-Specific Wrapper:
 * - Adds dealXgId, offerId to telemetry
 * - Configures dash-specific external library tagging
 * - Wraps shared implementation with business context
 *
 * Other apps (sales-checkin, etc.) create similar wrappers
 * with THEIR business context (e.g., checkinId, vin).
 *
 * This is a thin wrapper around the shared correlation middleware from @makemydeal/dr-shared-store
 * that configures it with dash-specific business context (dealXgId, offerId).
 *
 * **Telemetry Control (US1827639):**
 * Telemetry is controlled via server-side feature toggle:
 * - Feature Toggle: enableReduxTelemetry (default: OFF, fetched from feature toggles service)
 *
 * This allows:
 * - Server-controlled kill switch without rebuild/deploy
 *
 * When enabled, actions are enriched with:
 * - correlationId, actionType, timestamp, sessionId
 * - dealXgId, offerId (business context)
 * - callDepth, rootCorrelationId, parentCorrelationId (hierarchy tracking)
 * - New Relic PageActions and SPA interactions
 *
 * When disabled, only HTTP correlation is maintained for API debugging.
 *
 * For implementation details, see: @makemydeal/dr-shared-store/middleware/correlationMiddleware
 */
/**
 * Dash-specific business context for `ReduxAction` rows, shared by BOTH
 * emitters (`correlationMiddleware` rich rows and `reduxActionSpineMiddleware`
 * RSAA-lifecycle rows) so they group identically on `dealXgId`.
 *
 * Falls back to the URL/session dealXgId until the deal hydrates into Redux
 * state. Early-bootstrap actions (e.g. BOOTSTRAP_INITIAL_LOAD, the initDealXg
 * RSAA) dispatch BEFORE `getDealXgId(state)` is populated, so without this
 * fallback their telemetry is tagged with an empty dealXgId and drops out of
 * deal-scoped dashboards — leaving gaps at the lowest actionIndexes (e.g.
 * missing actionIndex 1). The URL param is known from first paint.
 */
const getReduxActionBusinessContext = (state: StateTree) => ({
    dealXgId: offerReduxSelectors.getDealXgId(state) || offerSessionUtils.getSessionValue('dealXgId') || undefined,
    offerId: offerInfoSelectors.getOfferId(state)
});

const baseCorrelationMiddleware: Middleware<Record<string, never>, StateTree> = createSharedCorrelationMiddleware<StateTree>({
    // Runtime feature toggle configuration (US1827639)
    // Server-controlled kill switch via feature toggles service
    enabled: (state) => {
        // Use optional chaining to access toggle directly, avoiding selector's precondition check.
        // Default OFF (honest kill-switch): until AppConfig loads the toggle, telemetry stays off —
        // consistent with the shared-store `isReduxTelemetryEnabled` selector and the OFF default of the
        // sibling snapshot/selector middlewares. Enable per-env via the `enableReduxTelemetry` config.
        // Type assertion needed: enableReduxTelemetry is in AppConfigDataOnlyToggles, which is part of
        // FeatureTogglesAndUrlOnly, but StateTree types featureToggles as Partial<AllFeatureToggles>
        return (state.featureToggles as Record<string, boolean | undefined>)?.enableReduxTelemetry ?? false;
    },

    // Store identifier for telemetry
    storeId: 'dash-store',

    // Extract dash-specific business context from state
    getBusinessContext: getReduxActionBusinessContext
});

/**
 * TEMPORARY WORKAROUND: Wrapper middleware to tag external library actions
 *
 * The @makemydeal/dr-offer-redux library dispatches actions without telemetry.
 * This wrapper intercepts actions before they reach the correlation middleware and
 * tags known external library actions with dispatchedBy metadata.
 *
 * TODO: Remove this wrapper when US1815738 is complete (external library updated with telemetry)
 * @see https://rally1.rallydev.com/userstory/US1815738
 */
export const correlationMiddleware: Middleware<Record<string, never>, StateTree> = (store) => {
    const baseMiddleware = baseCorrelationMiddleware(store);

    return (next) => {
        const baseNext = baseMiddleware(next);

        return (action) => {
            let modifiedAction = action;

            // Add actionStatus to all actions (US1815744)
            /* istanbul ignore next - Edge cases: action.meta undefined or RSAA actions */
            if (action.type && !action.meta?.actionStatus) {
                modifiedAction = {
                    ...modifiedAction,
                    meta: {
                        ...(modifiedAction.meta || {}),
                        actionStatus: getActionStatus(action.type)
                    }
                };
            } else if (!action.type && !action.meta?.actionStatus) {
                // RSAA actions don't have action.type initially, but we still need to set actionStatus
                // The shared middleware will create a synthetic actionType later (RSAA: <endpoint>)
                // which getActionStatus() will detect and return 202
                modifiedAction = {
                    ...modifiedAction,
                    meta: {
                        ...(modifiedAction.meta || {}),
                        actionStatus: ACTION_STATUS.ASYNC_ACCEPTED // All RSAA actions are async API calls
                    }
                };
            }

            // Tag external library actions (US1815739 - temporary workaround)
            /* istanbul ignore next - Edge case: external library action tagging */
            if (action.type && !modifiedAction.meta?.dispatchedBy && isExternalLibraryPaymentAction(action.type)) {
                modifiedAction = {
                    ...modifiedAction,
                    meta: {
                        ...(modifiedAction.meta || {}),
                        dispatchedBy: 'dr-offer-redux'
                    }
                };
            }

            return baseNext(modifiedAction);
        };
    };
};

/**
 * Dash-specific redux-action spine middleware — the DOWNSTREAM half of the
 * `ReduxAction` emitter pair.
 *
 * Completes the spine by emitting a lightweight row for the RSAA lifecycle FSAs
 * (`OFFER_SAVE_SUCCESS`, `PAYMENT_RECEIVED/FAILURE`, …) that `correlationMiddleware`
 * never sees — they are dispatched by `redux-api-middleware` via `next()`
 * downstream of it. With both active, `count(*) … WHERE actionName =
 * 'ReduxAction'` reflects every non-ignored dispatch, and each row carries
 * the monotonic `actionIndex` that `StateSnapshotDelta` / `SelectorDelta` join on.
 *
 * **Placement.** Must run immediately AFTER `redux-api-middleware` (so it
 * observes the lifecycle FSAs) and before the tail telemetry middlewares.
 *
 * **Telemetry control.** Gated on the SAME toggle + default (OFF until AppConfig
 * loads) as `correlationMiddleware` (`enableReduxTelemetry`) so the two emitters
 * are one coherent stream — never one without the other.
 */
export const reduxActionSpineMiddleware: Middleware<Record<string, never>, StateTree> = createReduxActionSpineMiddleware<StateTree>(
    {
        enabled: (state) => (state.featureToggles as Record<string, boolean | undefined>)?.enableReduxTelemetry ?? false,
        storeId: 'dash-store',
        getBusinessContext: getReduxActionBusinessContext
    }
);

/**
 * Re-export dispatchWithTelemetry from shared library for convenience
 *
 * This utility function enables middleware to dispatch child actions with proper
 * correlation hierarchy tracking. See @makemydeal/dr-shared-store for documentation.
 */
export { dispatchWithTelemetry } from '@makemydeal/dr-shared-store';
